export const getStoredToken = () => {
    const token = localStorage.getItem("token");

    if (!token || token === "undefined" || token === "null") {
        return null;
    }

    return token;
};

export const setStoredToken = (token) => {
    if (!token) {
        return;
    }

    localStorage.setItem("token", token);
};

// Only present for a session opened with supportsRefresh: true — a legacy
// (pre-refresh) login never sends one, so this stays absent for those.
export const getStoredRefreshToken = () => {
    const token = localStorage.getItem("refreshToken");
    if (!token || token === "undefined" || token === "null") {
        return null;
    }
    return token;
};

export const setStoredRefreshToken = (token) => {
    if (!token) {
        return;
    }
    localStorage.setItem("refreshToken", token);
};

// Replace the credential pair as one logical operation. In particular, an
// absent refresh token removes any previous value instead of silently keeping
// a stale token from another login.
export const setStoredSession = ({ token, refreshToken }) => {
    if (token) {
        localStorage.setItem("token", token);
    } else {
        localStorage.removeItem("token");
    }

    if (refreshToken) {
        localStorage.setItem("refreshToken", refreshToken);
    } else {
        localStorage.removeItem("refreshToken");
    }
};

export const clearStoredRefreshToken = () => {
    localStorage.removeItem("refreshToken");
};

/**
 * Whether the admin has already passed the statements password prompt.
 *
 * A module-level flag on purpose — deliberately NOT localStorage or
 * sessionStorage. It exists so that ONE prompt covers a whole visit to the
 * statements section: the gate used to hold this in component state, and the
 * list and the detail page each mount their own gate, so opening a statement
 * from the list asked for the password a second time.
 *
 * Kept as weak as it can be while still fixing that. It dies with the page —
 * a reload, a new tab or a closed browser all re-prompt — and logout clears it
 * explicitly below, so an unattended dashboard cannot be walked up to and
 * reopened without the password.
 */
let statementsUnlocked = false;

export const isStatementsUnlocked = () => statementsUnlocked;

export const markStatementsUnlocked = () => {
    statementsUnlocked = true;
};

export const clearStatementsUnlocked = () => {
    statementsUnlocked = false;
};

export const clearStoredAuth = () => {
    clearStatementsUnlocked();
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    // A terminal auth failure while impersonating must not leave a second,
    // hidden admin session recoverable in storage after the UI logs out.
    localStorage.removeItem("originalToken");
    localStorage.removeItem("originalRefreshToken");
    localStorage.removeItem("originalUserData");
    localStorage.removeItem("fcmToken");
};

export const getStoredOriginalToken = () => {
    const token = localStorage.getItem("originalToken");
    if (!token || token === "undefined" || token === "null") {
        return null;
    }
    return token;
};

export const setStoredOriginalToken = (token) => {
    if (!token) return;
    localStorage.setItem("originalToken", token);
};

export const clearStoredOriginalToken = () => {
    localStorage.removeItem("originalToken");
};

// The admin's own refresh token, held aside during impersonation exactly
// like originalToken holds the access token — otherwise a long
// impersonation session (the impersonated user's activity, not the admin's)
// outlasting the admin's 30-minute access token would leave stopImpersonation
// restoring a token with no way to refresh it, stranding the admin on a dead
// session the moment they try to return to their own account.
export const getStoredOriginalRefreshToken = () => {
    const token = localStorage.getItem("originalRefreshToken");
    if (!token || token === "undefined" || token === "null") {
        return null;
    }
    return token;
};

export const setStoredOriginalRefreshToken = (token) => {
    if (!token) return;
    localStorage.setItem("originalRefreshToken", token);
};

export const clearStoredOriginalRefreshToken = () => {
    localStorage.removeItem("originalRefreshToken");
};

export const setStoredOriginalSession = ({ token, refreshToken }) => {
    if (token) {
        localStorage.setItem("originalToken", token);
    } else {
        localStorage.removeItem("originalToken");
    }

    if (refreshToken) {
        localStorage.setItem("originalRefreshToken", refreshToken);
    } else {
        localStorage.removeItem("originalRefreshToken");
    }
};

export const getStoredOriginalUserData = () => {
    const value = localStorage.getItem("originalUserData");
    if (!value) return null;

    try {
        return JSON.parse(value);
    } catch {
        localStorage.removeItem("originalUserData");
        return null;
    }
};

export const setStoredOriginalUserData = (userData) => {
    if (userData) {
        localStorage.setItem("originalUserData", JSON.stringify(userData));
    } else {
        localStorage.removeItem("originalUserData");
    }
};

export const clearStoredImpersonation = () => {
    localStorage.removeItem("originalToken");
    localStorage.removeItem("originalRefreshToken");
    localStorage.removeItem("originalUserData");
};

export const restoreStoredOriginalSession = () => {
    const token = getStoredOriginalToken();
    if (!token) return false;

    setStoredSession({ token, refreshToken: getStoredOriginalRefreshToken() });
    clearStoredImpersonation();
    return true;
};
